import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, DollarSign, Plus, Unlock } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import RangoFechas, { rangoInicial } from '../../components/RangoFechas';
import InputMonto from '../../components/InputMonto';
import { money, moneyK, fecha, hoy, Title, Badge, Empty, ESTADO_COBRO } from './ui';

export default function AgenciaCobros() {
  const navigate = useNavigate();
  const [data, setData] = useState({ cobros: [], total: 0, cantidad: 0 });
  const [rango, setRango] = useState(() => rangoInicial('todo'));
  const [f, setF] = useState({ estado: 'abiertos', search: '', desde: '', hasta: '' });
  const [listas, setListas] = useState({ medios: [], servicios: [] });
  const [pago, setPago] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/agency/cobros', { params: { ...f, desde: rango.desde, hasta: rango.hasta } })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [f, rango.desde, rango.hasta]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get('/agency/listas').then(r => setListas(r.data)).catch(() => {}); }, []);

  const registrarPago = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/agency/cobros/${pago.id}/pagos`, pago);
      toast.success(data.aviso ? `Pago registrado · le avisamos a ${data.aviso.join(', ')}` : 'Pago registrado');
      setPago(null); load();
    }
    catch { toast.error('Error'); }
  };
  // el estado se puede cambiar siempre; para marcarlo pagado pedimos fecha, monto y medio
  const habilitar = async (c) => {
    const avisar = window.confirm(`Se habilita "${c.concepto}" de ${money(c.monto)} y pasa a ser cobrable.\n\n¿Le avisamos al cliente por mail?\n\nAceptar: se habilita y le avisamos.\nCancelar: se habilita sin avisarle.`);
    try {
      const { data } = await api.post(`/agency/cobros/${c.id}/habilitar`, { avisar });
      toast.success(data.aviso ? 'Habilitado · le avisamos a ' + data.aviso.join(', ') : 'Habilitado');
      load();
    } catch { toast.error('Error'); }
  };
  const marcarEstado = async (c, estado) => {
    if (estado === c.estado) return;
    if (estado === 'pagado') return setPago({ id: c.id, fecha: hoy(), monto: c.monto });
    try {
      const { data } = await api.put('/agency/cobros/' + c.id, { estado });
      toast.success(data.revertido ? 'Se quitó el pago registrado' : 'Estado actualizado');
      load();
    } catch { toast.error('Error'); }
  };

  const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '8px' };
  const td = { padding: '10px 8px', borderTop: '1px solid var(--border)', fontSize: 13 };
  const vencidos = data.cobros.filter(c => c.habilitado && ['pendiente', 'vencido'].includes(c.estado) && c.vencimiento && c.vencimiento.slice(0, 10) < hoy());

  return (
    <div>
      <Title>Cobros</Title>

      <RangoFechas valor={rango} onChange={setRango} />

      <div className="card" style={{ marginBottom: 16, padding: '12px 14px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 300 }}>
          <Search size={15} style={{ position: 'absolute', left: 11, top: 11, color: 'var(--text-muted)' }} />
          <input value={f.search} onChange={e => setF({ ...f, search: e.target.value })} placeholder="Buscar cliente o concepto" style={{ paddingLeft: 32, fontSize: 13 }} />
        </div>
        <select value={f.estado} onChange={e => setF({ ...f, estado: e.target.value })} style={{ width: 'auto', fontSize: 13 }}>
          <option value="abiertos">Por cobrar</option>
          <option value="a_pasar">A pasar (futuros)</option>
          <option value="">Todos</option>
          <option value="pagado">Pagados</option>
          <option value="pendiente">Pendientes</option>
          <option value="informado">Informados por el cliente</option>
          <option value="anulado">Anulados</option>
        </select>
        <div style={{ marginLeft: 'auto', fontSize: 13 }}>
          <b>{data.cantidad}</b> cobros · <b>{moneyK(data.total)}</b>
          {vencidos.length > 0 && <span style={{ color: '#ef4444', marginLeft: 10 }}>{vencidos.length} vencidos</span>}
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-container">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>Cliente</th><th style={th}>Concepto</th><th style={th}>Emitido</th><th style={th}>Vence</th>
              <th style={{ ...th, textAlign: 'right' }}>Monto</th><th style={th}>Estado</th><th style={th}>Factura</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={8} style={{ ...td, color: 'var(--text-muted)' }}>Cargando...</td></tr>
                : data.cobros.length === 0 ? <tr><td colSpan={8} style={td}><Empty>No hay cobros con ese filtro</Empty></td></tr>
                : data.cobros.map(c => {
                  const e = ESTADO_COBRO[c.estado] || {};
                  const vencido = c.habilitado && ['pendiente', 'vencido'].includes(c.estado) && c.vencimiento && c.vencimiento.slice(0, 10) < hoy();
                  return (
                    <tr key={c.id}>
                      <td style={{ ...td, fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate('/agencia/clientes/' + c.cliente_id)}>{c.cliente}</td>
                      <td style={td}>{c.concepto || c.servicio || '—'}{c.cuota_total ? ` (${c.cuota_numero}/${c.cuota_total})` : ''}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 12, color: 'var(--text-muted)' }}>{c.emitido ? fecha(c.emitido) : '—'}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: vencido ? '#ef4444' : 'inherit' }}>{fecha(c.vencimiento)}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(c.monto)}</td>
                      <td style={td}>
                        {!c.habilitado && <div><Badge label="A pasar" color="#64748b" bg="rgba(100,116,139,0.15)" /></div>}
                        <select value={c.estado} onChange={ev => marcarEstado(c, ev.target.value)} title="Cambiar estado"
                          style={{ width: 'auto', padding: '4px 26px 4px 10px', fontSize: 12, fontWeight: 700, borderRadius: 999,
                            background: (vencido && c.estado === 'pendiente' ? 'rgba(239,68,68,0.15)' : e.bg) || 'transparent',
                            color: vencido && c.estado === 'pendiente' ? '#ef4444' : e.color, border: '1px solid var(--border)' }}>
                          {Object.entries(ESTADO_COBRO).map(([k, v]) => <option key={k} value={k}>{k === 'pendiente' && vencido ? 'Vencido' : v.label}</option>)}
                        </select>
                      </td>
                      <td style={{ ...td, fontSize: 12, color: 'var(--text-muted)' }}>{c.sin_factura ? 'Sin factura' : 'Con factura'}</td>
                      <td style={td}>
                        {!c.habilitado
                          ? <button className="btn btn-ghost btn-sm" onClick={() => habilitar(c)} title="Pasarlo al cliente"><Unlock size={14} /> Habilitar</button>
                          : c.estado !== 'pagado'
                          ? <button className="btn btn-ghost btn-sm" onClick={() => setPago({ id: c.id, fecha: hoy(), monto: c.monto })}><DollarSign size={14} /> Pagó</button>
                          : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Pagado el {fecha(c.pagado_el)}</span>}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {pago && (
        <div className="modal-overlay" onClick={() => setPago(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <h2>Registrar pago</h2>
            <form onSubmit={registrarPago}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>Fecha</label><input type="date" value={pago.fecha} onChange={e => setPago({ ...pago, fecha: e.target.value })} /></div>
                <div className="form-group"><label>Monto</label><InputMonto value={pago.monto} onChange={v => setPago({ ...pago, monto: v })} /></div>
              </div>
              <div className="form-group"><label>Medio de pago</label>
                <select value={pago.medio_id || ''} onChange={e => setPago({ ...pago, medio_id: e.target.value })}>
                  <option value="">—</option>{listas.medios.map(m => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                </select>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={pago.avisar !== false} onChange={e => setPago({ ...pago, avisar: e.target.checked })} style={{ width: 16, height: 16 }} />
                Avisarle al cliente que recibimos el pago
              </label>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setPago(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary"><Plus size={14} /> Registrar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
